import React, { useEffect, useState, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import axios from '../../axios';
import { IPromptFragment, IOrganization, IService } from '../../interfaces/domain';
import { EPromptFragmentUsage } from '../../enums';
import { Routes } from '../../classes/Routes';
import Alert from '../../components/UI/Alert/Alert';
import Button, { EButtonColor } from '../../components/UI/Button/Button';
import Container from '../../components/UI/Container/Container';
import Spinner from '../../components/UI/Spinner/Spinner';
import Input, { EInputType, IOption } from '../../components/UI/Input/Input';
import InputGroup from '../../components/UI/InputGroup/InputGroup';
import ModalContext, { EModalSize } from '../../components/UI/Modal/ModalContext';
import { ETranslation } from '../../translations/translation-keys';

interface MatchParams {
  id: string;
}

const PromptFragmentEdit: React.FC<RouteComponentProps<MatchParams>> = ({ match, history }) => {
  const { t } = useTranslation();
  const { setModal, closeModal } = useContext(ModalContext);
  
  const [fragment, setFragment] = useState<IPromptFragment>({
    id: "0",
    usage: EPromptFragmentUsage.Offer,
    text: ""
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [operators, setOperators] = useState<IOrganization[]>([]);
  const [services, setServices] = useState<IService[]>([]);

  const isNew = Routes.isNew(match.params.id);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [operatorsRes, servicesRes] = await Promise.all([
        axios.get('/json/organizations/list?level=OPERATOR'),
        axios.post('/json/services/list', {})
      ]);
      setOperators(operatorsRes.data || []);
      setServices(servicesRes.data || []);

      if (!isNew) {
        const res = await axios.get(`/json/promptfragments/get?id=${match.params.id}`);
        setFragment(res.data);
      }
      setError(null);
    } catch (err: any) {
      setError(err.response?.data || "Virhe ladattaessa tietoja");
    } finally {
      setLoading(false);
    }
  }, [isNew, match.params.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const saveHandler = async () => {
    setSaving(true);
    try {
      const endpoint = isNew ? '/json/promptfragments/add' : '/json/promptfragments/update';
      const payload = {
        id: isNew ? null : fragment.id,
        operator: fragment.operator?.id || null,
        service: fragment.service?.id || null,
        usage: fragment.usage,
        text: fragment.text
      };
      if (isNew) {
        await axios.post(endpoint, payload);
      } else {
        await axios.put(endpoint, payload);
      }
      history.push(Routes.PROMPT_FRAGMENTS);
    } catch (err: any) {
      setError(err.response?.data || "Virhe tallennettaessa");
      setSaving(false);
    }
  };

  const deleteConfirmHandler = () => {
    setModal({
      isOpen: true,
      title: "Vahvista poisto",
      content: (
        <div>
          <p>Haluatko varmasti poistaa tämän promptin?</p>
          <InputGroup>
            <Button color={EButtonColor.DANGER} onClick={deleteHandler}>Kyllä, poista</Button>
            <Button onClick={closeModal}>{t(ETranslation.COMMON_CANCEL)}</Button>
          </InputGroup>
        </div>
      ),
      size: EModalSize.SMALL
    });
  };

  const deleteHandler = async () => {
    closeModal();
    setSaving(true);
    try {
      await axios.delete(`/json/promptfragments/delete?id=${fragment.id}`);
      history.push(Routes.PROMPT_FRAGMENTS);
    } catch (err: any) {
      setError(err.response?.data || "Virhe poistettaessa");
      setSaving(false);
    }
  };

  if (loading) {
    return <Container><Spinner /></Container>;
  }

  const getSelectOptions = (items: any[]): IOption[] => {
    return [{ value: "", label: "Valitse..." }, ...items.map(i => ({ value: String(i.id), label: i.name }))];
  };

  return (
    <Container>
      {error && <Alert>{error}</Alert>}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>{isNew ? "Uusi Prompti" : "Muokkaa Promptia"}</h2>
        {!isNew && <Button color={EButtonColor.DANGER} onClick={deleteConfirmHandler}>{t(ETranslation.COMMON_REMOVE)}</Button>}
      </div>
      
      <Input
        type={EInputType.select}
        inputName="operator"
        label="Operaattori"
        value={fragment.operator?.id ? String(fragment.operator.id) : ""}
        options={getSelectOptions(operators)}
        onChange={(val) => setFragment({ ...fragment, operator: operators.find(o => String(o.id) === String(val)) })}
      />
      
      <Input
        type={EInputType.select}
        inputName="service"
        label="Palvelu (Valinnainen)"
        value={fragment.service?.id ? String(fragment.service.id) : ""}
        options={getSelectOptions(services)}
        onChange={(val) => setFragment({ ...fragment, service: services.find(s => String(s.id) === String(val)) })}
      />

      <Input
        type={EInputType.select}
        inputName="usage"
        label="Käyttötarkoitus"
        value={fragment.usage || ""}
        options={[
          { value: EPromptFragmentUsage.Offer, label: EPromptFragmentUsage.Offer },
          { value: EPromptFragmentUsage.SystemInstruction, label: EPromptFragmentUsage.SystemInstruction }
        ]}
        onChange={(val) => setFragment({ ...fragment, usage: val as EPromptFragmentUsage })}
      />

      <Input
        type={EInputType.textarea}
        inputName="text"
        label="Promptiteksti"
        value={fragment.text || ""}
        onChange={(val) => setFragment({ ...fragment, text: val as string })}
      />

      <InputGroup>
        <Button onClick={saveHandler} disabled={saving}>{t(ETranslation.COMMON_SAVE)}</Button>
        <Button onClick={() => history.push(Routes.PROMPT_FRAGMENTS)} disabled={saving}>{t(ETranslation.COMMON_CANCEL)}</Button>
      </InputGroup>
    </Container>
  );
};

export default PromptFragmentEdit;
